import {
  InvoiceSheet,
  PurchaseOrderSheet,
  SalesOrderSheet,
} from "@/components/operations";
import {
  InventoryWorkspaceInventoryEditSheet,
  InventoryWorkspaceInvoiceCreateSheet,
  InventoryWorkspacePurchaseOrderCreateSheet,
  InventoryWorkspaceSalesOrderCreateSheet,
  InventoryWorkspaceStockCheckSheet,
} from "@/components/operations/InventoryWorkspaceActionSheets";
import {
  formatCurrency,
  formatNumber,
  formatStatusLabel,
  getClearedInvoiceAmountsById,
  getInventoryBookingBlockDetails,
  getInventoryOperationalStatus,
  getOrderInvoiceSummary,
  getPurchaseOrderDisplayStatus,
} from "@/components/operations/operations-shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  useAccountIssuesByInventoryId,
  useAllClients,
  useAllVendors,
  useGetAuditLogsByInventoryId,
  useInventoryById,
  useInvoicesByInventoryId,
  useLogSensitiveReveal,
  usePayments,
  usePurchaseOrdersByInventoryId,
  useRecordAccountIssue,
  useSalesOrdersByInventoryId,
  useStockChecksByInventoryId,
  useUpdateInventory,
  useUsers,
} from "@/hooks";
import { formatRelativeTimestamp, timestampToDate } from "@/utils/formatting";
import { getPurchaseOrderTotal, getSalesOrderTotal } from "@/utils/order-total";
import {
  formatValue as formatAuditValue,
  humanizeFieldName,
} from "@/components/utils/auditlog-utlis";
import { AuditLogAction } from "@/core";
import { ArrowRight, Check, ChevronDown, ChevronRight, Copy, Eye, EyeOff } from "lucide-react";
import { Fragment, type ReactNode, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";

type SectionId =
  | "purchase-orders"
  | "purchase-financials"
  | "inventory"
  | "account-issues"
  | "sell-orders"
  | "sell-financials"
  | "audit-log";

function WorkspaceSection(props: {
  id: SectionId;
  title: string;
  description: string;
  active: boolean;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const { id, title, description, active, actions, children } = props;

  return (
    <Card
      id={id}
      className={active ? "border-primary/40 shadow-sm shadow-primary/10" : ""}
    >
      <CardHeader className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function formatShortId(value: string | undefined) {
  return value?.slice(0, 8) || "Unlinked";
}

function getAuditEntityLabel(value: string | undefined) {
  switch (value) {
    case "purchase_order":
      return "PO";
    case "sales_order":
      return "SO";
    case "stock_check":
      return "Stock Check";
    default:
      return formatStatusLabel(value);
  }
}

function getAuditPerformedByLabel(payload: {
  performedByType?: "user" | "system";
  user?: { email?: string; id: string };
}) {
  if (payload.performedByType === "system") {
    return "System";
  }

  return payload.user?.email || payload.user?.id || "Unknown user";
}

export default function InventoryWorkspacePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const inventoryId = searchParams.get("inventoryId") || "";
  const selectedPoId = searchParams.get("purchaseOrderId") || "";
  const selectedSoId = searchParams.get("salesOrderId") || "";
  const selectedInvoiceId = searchParams.get("invoiceId") || "";
  const focus = (searchParams.get("focus") as SectionId | null) || "inventory";
  const requestedSheetType = searchParams.get("sheet");
  const activeSheetType =
    requestedSheetType === "purchaseOrder" && selectedPoId
      ? "purchaseOrder"
      : requestedSheetType === "salesOrder" && selectedSoId
        ? "salesOrder"
        : requestedSheetType === "invoice" && selectedInvoiceId
          ? "invoice"
          : null;

  const [createPoOpen, setCreatePoOpen] = useState(false);
  const [createSoOpen, setCreateSoOpen] = useState(false);
  const [editInventoryOpen, setEditInventoryOpen] = useState(false);
  const [stockCheckOpen, setStockCheckOpen] = useState(false);
  const [createPoInvoiceOpen, setCreatePoInvoiceOpen] = useState(false);
  const [createSoInvoiceOpen, setCreateSoInvoiceOpen] = useState(false);
  const [defaultPoInvoiceOrderId, setDefaultPoInvoiceOrderId] = useState("");
  const [defaultSoInvoiceOrderId, setDefaultSoInvoiceOrderId] = useState("");
  const [showAccountPassword, setShowAccountPassword] = useState(false);
  const [expandedAuditLogId, setExpandedAuditLogId] = useState<string | null>(null);
  const [updateProblemStatusOpen, setUpdateProblemStatusOpen] = useState(false);
  const [problemStatusInput, setProblemStatusInput] = useState<
    "cleared" | "audited" | "issue" | "suspended"
  >("cleared");
  const [problemStatusRemarks, setProblemStatusRemarks] = useState("");
  const [emailCopied, setEmailCopied] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");

  const inventoryQuery = useInventoryById(inventoryId);
  const purchaseOrdersQuery = usePurchaseOrdersByInventoryId(inventoryId);
  const salesOrdersQuery = useSalesOrdersByInventoryId(inventoryId);
  const invoicesQuery = useInvoicesByInventoryId(inventoryId);
  const stockChecksQuery = useStockChecksByInventoryId(inventoryId);
  const accountIssuesQuery = useAccountIssuesByInventoryId(inventoryId);
  const auditLogsQuery = useGetAuditLogsByInventoryId(inventoryId);
  const paymentsQuery = usePayments({
    pagination: { limit: 500 },
    orderBy: { field: "createdAt", direction: "desc" },
  });
  const vendorsQuery = useAllVendors();
  const clientsQuery = useAllClients();
  const usersQuery = useUsers();
  const logSensitiveRevealMutation = useLogSensitiveReveal();
  const updateInventoryMutation = useUpdateInventory();
  const recordAccountIssueMutation = useRecordAccountIssue();

  const inventory = inventoryQuery.data;
  const isArchived = inventory?.status === "archived";
  const inventoryBookingBlock = inventory
    ? getInventoryBookingBlockDetails({ inventory })
    : null;
  const purchaseOrders = purchaseOrdersQuery.data || [];
  const salesOrders = salesOrdersQuery.data || [];
  const invoices = invoicesQuery.data || [];
  const stockChecks = stockChecksQuery.data || [];
  const accountIssues = accountIssuesQuery.data || [];
  const auditLogs = auditLogsQuery.data || [];
  const vendors = useMemo(
    () => vendorsQuery.data?.pages.flat() ?? [],
    [vendorsQuery.data],
  );
  const clients = useMemo(
    () => clientsQuery.data?.pages.flat() ?? [],
    [clientsQuery.data],
  );
  const users = usersQuery.data ?? [];
  const payments = useMemo(
    () => paymentsQuery.data?.pages.flat() ?? [],
    [paymentsQuery.data],
  );

  const poPayments = payments.filter(
    (payment) => payment.appliesToType === "po",
  );
  const clearedByInvoiceId = useMemo(
    () => getClearedInvoiceAmountsById({ payments }),
    [payments],
  );

  const purchaseInvoices = invoices.filter((invoice) => Boolean(invoice.poId));
  const sellInvoices = invoices.filter((invoice) => Boolean(invoice.soId));
  const totalPurchaseMiles = purchaseOrders.reduce(
    (sum, purchaseOrder) => sum + purchaseOrder.amountBought,
    0,
  );

  const purchaseFinancialRows = purchaseOrders.map((purchaseOrder) => {
    const paid = poPayments
      .filter(
        (payment) =>
          payment.appliesToId === purchaseOrder.id &&
          payment.status === "cleared" &&
          payment.direction !== "incoming" &&
          // Sourced payments (PayPal sync) are context-only, not counted.
          !payment.source,
      )
      .reduce((sum, payment) => sum + payment.amount, 0);

    return {
      purchaseOrder,
      paid,
      outstanding: Math.max(
        0,
        getPurchaseOrderTotal({
          buyPrice: purchaseOrder.buyPrice,
          taxCost: purchaseOrder.taxCost,
          totalAmount: purchaseOrder.totalAmount,
        }) - paid,
      ),
    };
  });

  const sellFinancialRows = salesOrders.map((salesOrder) => {
    const invoiceSummary = getOrderInvoiceSummary({
      kind: "so",
      orderId: salesOrder.id,
      invoices: sellInvoices,
      clearedByInvoiceId,
    });

    return {
      salesOrder,
      ...invoiceSummary,
    };
  });

  const purchaseInvoiceRows = purchaseInvoices.map((invoice) => {
    const linkedPurchaseOrder = purchaseOrders.find(
      (purchaseOrder) => purchaseOrder.id === invoice.poId,
    );
    const cleared = clearedByInvoiceId.get(invoice.id) || 0;

    return {
      invoice,
      linkedPurchaseOrder,
      cleared,
      outstanding: Math.max(0, invoice.amountDue - cleared),
    };
  });

  const sellInvoiceRows = sellInvoices.map((invoice) => {
    const linkedSalesOrder = salesOrders.find(
      (salesOrder) => salesOrder.id === invoice.soId,
    );
    const cleared = clearedByInvoiceId.get(invoice.id) || 0;

    return {
      invoice,
      linkedSalesOrder,
      cleared,
      outstanding: Math.max(0, invoice.amountDue - cleared),
    };
  });

  const purchaseTotalAmount = purchaseFinancialRows.reduce(
    (sum, row) =>
      sum +
      getPurchaseOrderTotal({
        buyPrice: row.purchaseOrder.buyPrice,
        taxCost: row.purchaseOrder.taxCost,
        totalAmount: row.purchaseOrder.totalAmount,
      }),
    0,
  );
  const purchaseClearedAmount = purchaseInvoiceRows.reduce(
    (sum, row) => sum + row.cleared,
    0,
  );
  const purchaseSummary = {
    totalAmount: purchaseTotalAmount,
    cleared: purchaseClearedAmount,
    outstanding: Math.max(0, purchaseTotalAmount - purchaseClearedAmount),
  };
  const inventoryBuyCpm =
    totalPurchaseMiles > 0
      ? purchaseSummary.totalAmount / totalPurchaseMiles
      : inventory?.sourceDealSnapshot?.unitPrice;

  const sellSummary = {
    totalAmount: sellFinancialRows.reduce(
      (sum, row) =>
        sum +
        getSalesOrderTotal({
          revenue: row.salesOrder.revenue,
          taxCost: row.salesOrder.taxCost,
          totalAmount: row.salesOrder.totalAmount,
        }),
      0,
    ),
    invoiced: sellFinancialRows.reduce((sum, row) => sum + row.invoiced, 0),
    cleared: sellFinancialRows.reduce((sum, row) => sum + row.cleared, 0),
    outstanding: sellFinancialRows.reduce(
      (sum, row) => sum + row.outstanding,
      0,
    ),
  };

  useEffect(() => {
    setShowAccountPassword(false);
    setEmailCopied(false);
    setPasswordCopied(false);
  }, [inventoryId]);

  useEffect(() => {
    if (!isEditingNotes) {
      setNotesDraft(inventory?.notes || "");
    }
  }, [inventory?.notes, isEditingNotes]);

  const logPasswordReview = () => {
    if (!inventory?.id || !inventory.accountPassword) {
      return;
    }

    logSensitiveRevealMutation.mutate({
      collection: "inventories",
      documentId: inventory.id,
      field: "accountPassword",
    });
  };

  const handleToggleAccountPassword = () => {
    setShowAccountPassword((previous) => {
      if (!previous) {
        logPasswordReview();
      }

      return !previous;
    });
  };

  const handleCopyAccountPassword = async () => {
    if (!inventory?.accountPassword) {
      return;
    }

    logPasswordReview();

    try {
      await navigator.clipboard.writeText(inventory.accountPassword);
      setPasswordCopied(true);
      window.setTimeout(() => setPasswordCopied(false), 2000);
    } catch {
      setPasswordCopied(false);
    }
  };

  const handleCopyAccountEmail = async () => {
    if (!inventory?.accountEmail) {
      return;
    }

    try {
      await navigator.clipboard.writeText(inventory.accountEmail);
      setEmailCopied(true);
      window.setTimeout(() => setEmailCopied(false), 2000);
    } catch {
      setEmailCopied(false);
    }
  };

  const handleStartNotesEdit = () => {
    setNotesDraft(inventory?.notes || "");
    setIsEditingNotes(true);
  };

  const handleCancelNotesEdit = () => {
    setNotesDraft(inventory?.notes || "");
    setIsEditingNotes(false);
  };

  const handleSaveNotes = async () => {
    if (!inventory) {
      return;
    }

    await updateInventoryMutation.mutateAsync({
      id: inventory.id,
      data: {
        notes: notesDraft?.trim() || undefined,
      },
    });

    setIsEditingNotes(false);
  };

  const openWorkspaceSheet = (payload: {
    kind: "purchaseOrder" | "salesOrder" | "invoice";
    id: string;
    sectionFocus: SectionId;
  }) => {
    const { kind, id, sectionFocus } = payload;
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("purchaseOrderId");
        next.delete("salesOrderId");
        next.delete("invoiceId");
        next.delete("sheet");
        next.set("inventoryId", inventoryId);
        next.set("focus", sectionFocus);
        next.set(
          kind === "purchaseOrder"
            ? "purchaseOrderId"
            : kind === "salesOrder"
              ? "salesOrderId"
              : "invoiceId",
          id,
        );
        next.set("sheet", kind);
        return next;
      },
      { replace: true },
    );
  };

  const closeWorkspaceSheet = (
    kind: "purchaseOrder" | "salesOrder" | "invoice",
  ) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (next.get("sheet") === kind) {
          next.delete("sheet");
        }
        return next;
      },
      { replace: true },
    );
  };

  if (!inventoryId) {
    return <Navigate to="/operations/inventory" replace />;
  }

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 space-y-6">
      {isArchived && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          This inventory is archived. All actions are disabled. Unarchive it from the inventory sheet to resume operations.
        </div>
      )}
      <WorkspaceSection
        id="purchase-orders"
        title="Purchase Orders"
        description="View and manage purchase orders linked to this inventory."
        active={focus === "purchase-orders"}
        actions={
          <Button size="sm" onClick={() => setCreatePoOpen(true)} disabled={isArchived}>
            New PO
          </Button>
        }
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead className="text-right">Miles</TableHead>
              <TableHead className="text-right">Buy Price</TableHead>
              <TableHead className="w-[220px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {purchaseOrders.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-muted-foreground"
                >
                  No purchase orders linked to this inventory.
                </TableCell>
              </TableRow>
            ) : (
              purchaseOrders.map((purchaseOrder) => (
                <TableRow key={purchaseOrder.id}>
                  <TableCell className="font-mono text-xs">
                    {formatShortId(purchaseOrder.id)}
                  </TableCell>
                  <TableCell>
                    {purchaseOrder.vendorId ? (
                      <Link
                        className="text-primary underline-offset-4 hover:underline"
                        to={`/vendors?vendorId=${purchaseOrder.vendorId}&poId=${purchaseOrder.id}`}
                      >
                        {purchaseOrder.vendorDisplayName ||
                          purchaseOrder.vendorEmail ||
                          "Unknown vendor"}
                      </Link>
                    ) : (
                      purchaseOrder.vendorDisplayName ||
                      purchaseOrder.vendorEmail ||
                      "Unknown vendor"
                    )}
                  </TableCell>
                  <TableCell>
                    {formatStatusLabel(getPurchaseOrderDisplayStatus(purchaseOrder))}
                  </TableCell>
                  <TableCell>
                    {formatStatusLabel(purchaseOrder.paymentStatus)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatNumber({
                      value: Math.max(
                        0,
                        purchaseOrder.amountBought -
                          (purchaseOrder.milesReturned ?? 0),
                      ),
                    })}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency({
                      value: Math.max(
                        0,
                        getPurchaseOrderTotal({
                          buyPrice: purchaseOrder.buyPrice,
                          taxCost: purchaseOrder.taxCost,
                          totalAmount: purchaseOrder.totalAmount,
                        }) - (purchaseOrder.refundAmount ?? 0),
                      ),
                    })}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          openWorkspaceSheet({
                            kind: "purchaseOrder",
                            id: purchaseOrder.id,
                            sectionFocus: "purchase-orders",
                          })
                        }
                      >
                        Open
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isArchived}
                        onClick={() => {
                          setDefaultPoInvoiceOrderId(purchaseOrder.id);
                          setCreatePoInvoiceOpen(true);
                        }}
                      >
                        New Invoice
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </WorkspaceSection>

      <WorkspaceSection
        id="purchase-financials"
        title="Purchase Order Financials"
        description="Manage payables, purchase invoices, and outstanding balances in context."
        active={focus === "purchase-financials"}
        actions={
          <Button
            size="sm"
            onClick={() => {
              setDefaultPoInvoiceOrderId(purchaseOrders[0]?.id || "");
              setCreatePoInvoiceOpen(true);
            }}
            disabled={isArchived || purchaseOrders.length === 0}
          >
            Add Invoice
          </Button>
        }
      >
        <div className="flex flex-wrap divide-y rounded-lg border mb-4 md:divide-x md:divide-y-0">
          {[
            {
              label: "Total",
              value: formatCurrency({ value: purchaseSummary.totalAmount }),
            },
            {
              label: "Cleared",
              value: formatCurrency({ value: purchaseSummary.cleared }),
            },
            {
              label: "Outstanding",
              value: formatCurrency({ value: purchaseSummary.outstanding }),
            },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="flex flex-col gap-0.5 px-4 py-3 w-1/2 md:w-auto md:flex-1"
            >
              <span className="text-xs text-muted-foreground">{label}</span>
              <span className="text-sm font-semibold">{value}</span>
            </div>
          ))}
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Purchase Order</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Amount Due</TableHead>
              <TableHead className="text-right">Cleared</TableHead>
              <TableHead className="text-right">Outstanding</TableHead>
              <TableHead className="w-[220px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {purchaseInvoiceRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-muted-foreground"
                >
                  No purchase invoices linked to this inventory yet.
                </TableCell>
              </TableRow>
            ) : (
              purchaseInvoiceRows.map((row) => (
                <TableRow key={row.invoice.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>
                        {row.linkedPurchaseOrder?.vendorDisplayName ||
                          row.linkedPurchaseOrder?.vendorEmail ||
                          "Unlinked"}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {formatShortId(row.invoice.poId)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{formatShortId(row.invoice.id)}</TableCell>
                  <TableCell>{formatStatusLabel(row.invoice.status)}</TableCell>
                  <TableCell className="text-right">
                    {formatCurrency({ value: row.invoice.amountDue })}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency({ value: row.cleared })}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency({ value: row.outstanding })}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          openWorkspaceSheet({
                            kind: "invoice",
                            id: row.invoice.id,
                            sectionFocus: "purchase-financials",
                          })
                        }
                      >
                        Open Invoice
                      </Button>
                      {row.invoice.poId ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            openWorkspaceSheet({
                              kind: "purchaseOrder",
                              id: row.invoice.poId!,
                              sectionFocus: "purchase-orders",
                            })
                          }
                        >
                          Open PO
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </WorkspaceSection>

      <WorkspaceSection
        id="inventory"
        title="Inventory"
        description="Edit the inventory, manage credentials, notes, and record stock checks."
        active={focus === "inventory"}
        actions={
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditInventoryOpen(true)}
              disabled={!inventory || isArchived}
            >
              Edit Inventory
            </Button>
            <Button
              size="sm"
              onClick={() => setStockCheckOpen(true)}
              disabled={!inventory || isArchived}
            >
              Add Stock Check
            </Button>
          </>
        }
      >
        {!inventory ? (
          <div className="text-muted-foreground">
            {inventoryQuery.isLoading
              ? "Loading inventory..."
              : "Inventory not found."}
          </div>
        ) : (
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Account Credentials</CardTitle>
                <CardDescription>
                  Review and copy the stored login email and password without
                  opening edit mode.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-lg border bg-muted/30 px-4 py-3">
                    <div className="flex items-end justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-xs text-muted-foreground">
                          Email
                        </div>
                        <div className="mt-1 break-all font-mono text-sm">
                          {inventory.accountEmail || "No email recorded"}
                        </div>
                      </div>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8 shrink-0"
                        onClick={handleCopyAccountEmail}
                        disabled={!inventory.accountEmail}
                        title="Copy email"
                        aria-label="Copy email"
                      >
                        {emailCopied ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="rounded-lg border bg-muted/30 px-4 py-3">
                    <div className="flex items-end justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-xs text-muted-foreground">
                          Username
                        </div>
                        <div className="mt-1 break-all font-mono text-sm">
                          {inventory.username || "No username recorded"}
                        </div>
                      </div>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8 shrink-0"
                        onClick={() => {
                          if (inventory.username) {
                            navigator.clipboard.writeText(inventory.username);
                          }
                        }}
                        disabled={!inventory.username}
                        title="Copy username"
                        aria-label="Copy username"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="rounded-lg border bg-muted/30 px-4 py-3">
                    <div className="flex items-end justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-xs text-muted-foreground">
                          Password
                        </div>
                        <div className="mt-1 break-all font-mono text-sm">
                          {inventory.accountPassword
                            ? showAccountPassword
                              ? inventory.accountPassword
                              : "••••••••••••"
                            : "No password recorded"}
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          onClick={handleToggleAccountPassword}
                          disabled={!inventory.accountPassword}
                          title={
                            showAccountPassword
                              ? "Hide password"
                              : "Reveal password"
                          }
                          aria-label={
                            showAccountPassword
                              ? "Hide password"
                              : "Reveal password"
                          }
                        >
                          {showAccountPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          onClick={handleCopyAccountPassword}
                          disabled={!inventory.accountPassword}
                          title="Copy password"
                          aria-label="Copy password"
                        >
                          {passwordCopied ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-wrap divide-y rounded-lg border md:divide-x md:divide-y-0">
              {[
                {
                  label: "Status",
                  value: formatStatusLabel(
                    getInventoryOperationalStatus({ inventory }),
                  ),
                },
                {
                  label: "Buy CPM",
                  value:
                    inventoryBuyCpm !== undefined
                      ? `${(inventoryBuyCpm * 100).toFixed(5)}¢`
                      : "-",
                },
                {
                  label: "Status Tier",
                  value: inventory.statusTier
                    ? `${inventory.statusTier.name} · ${inventory.statusTier.percentage}%`
                    : "-",
                },
                {
                  label: "Blocked Until",
                  value: inventoryBookingBlock?.tbd
                    ? "TBD"
                    : inventoryBookingBlock?.blockedUntil
                      ? inventoryBookingBlock.blockedUntil.toLocaleDateString()
                      : "-",
                },
                {
                  label: "Account Status",
                  value: formatStatusLabel(inventory.accountStatus),
                },
                {
                  label: "Last Booking",
                  value:
                    timestampToDate(
                      inventory.lastBooking,
                    )?.toLocaleDateString() ?? "-",
                },
                {
                  label: "Purchased",
                  value: formatNumber({ value: inventory.totalPurchasedMiles }),
                },
                {
                  label: "Sold",
                  value: formatNumber({ value: inventory.totalSoldMiles }),
                },
                {
                  label: "Available",
                  value: formatNumber({ value: inventory.availableMiles }),
                },
                {
                  label: "Account Balance",
                  value:
                    inventory.accountBalance == null
                      ? "-"
                      : formatNumber({ value: inventory.accountBalance }),
                },
                {
                  label: "Variance",
                  value:
                    inventory.accountBalance == null
                      ? "-"
                      : formatNumber({
                          value:
                            inventory.accountBalance - inventory.availableMiles,
                        }),
                },
                {
                  label: "2FA",
                  value: formatStatusLabel(inventory.twoFaStatus),
                },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="flex flex-col gap-0.5 px-4 py-3 w-1/2 md:w-auto md:flex-1"
                >
                  <span className="text-xs text-muted-foreground">{label}</span>
                  <span className="text-sm font-semibold">{value}</span>
                </div>
              ))}
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Owner</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>PayPal</TableHead>
                  <TableHead>DOB</TableHead>
                  <TableHead>IP Location</TableHead>
                  <TableHead>GoLogin</TableHead>
                  <TableHead>PO Count</TableHead>
                  <TableHead>SO Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>{inventory.accountOwnerName || "-"}</TableCell>
                  <TableCell>{inventory.accountEmail || "-"}</TableCell>
                  <TableCell>{inventory.username || "-"}</TableCell>
                  <TableCell>{inventory.paypalEmail || "-"}</TableCell>
                  <TableCell>
                    {timestampToDate(inventory.dob)?.toLocaleDateString() ??
                      "-"}
                  </TableCell>
                  <TableCell>{inventory.ipLocation || "-"}</TableCell>
                  <TableCell>
                    {inventory.gologinProfileId
                      ? "Configured"
                      : "Not configured"}
                  </TableCell>
                  <TableCell>{purchaseOrders.length}</TableCell>
                  <TableCell>{salesOrders.length}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {inventory.expirationInfo ? (
              <div className="rounded-xl border p-4 space-y-1">
                <div className="text-sm font-medium text-muted-foreground">
                  Expiration Info
                </div>
                <div className="text-sm whitespace-pre-wrap">
                  {inventory.expirationInfo}
                </div>
              </div>
            ) : null}

            <Card>
              <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
                <div>
                  <CardTitle className="text-base">Notes</CardTitle>
                  <CardDescription>
                    Capture quick operational context without leaving the
                    workspace.
                  </CardDescription>
                </div>
                {isEditingNotes ? (
                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelNotesEdit}
                      disabled={updateInventoryMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        void handleSaveNotes();
                      }}
                      loading={updateInventoryMutation.isPending}
                      loadingText="Saving..."
                    >
                      Save
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleStartNotesEdit}
                  >
                    {inventory.notes?.trim() ? "Edit Note" : "Add Note"}
                  </Button>
                )}
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                {isEditingNotes ? (
                  <Textarea
                    value={notesDraft}
                    onChange={(event) => setNotesDraft(event.target.value)}
                    placeholder="Add operational notes, access context, or follow-up tasks"
                    className="min-h-32 text-sm text-foreground"
                  />
                ) : (
                  inventory.notes || "No inventory notes recorded yet."
                )}
              </CardContent>
            </Card>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Checked At</TableHead>
                  <TableHead className="text-right">Account Balance</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">Variance</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stockChecks.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-muted-foreground"
                    >
                      No stock checks recorded for this inventory yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  stockChecks.map((stockCheck) => (
                    <TableRow key={stockCheck.id}>
                      <TableCell>
                        {stockCheck.checkedAt?.toLocaleString() || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber({ value: stockCheck.reportedBalance })}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber({
                          value: stockCheck.systemBalanceAtCheck,
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber({
                          value:
                            stockCheck.reportedBalance -
                            stockCheck.systemBalanceAtCheck,
                        })}
                      </TableCell>
                      <TableCell>{stockCheck.notes || "-"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </WorkspaceSection>

      <WorkspaceSection
        id="account-issues"
        title={`Account Issues${
          inventory?.problemStatus
            ? ` (${formatStatusLabel(inventory.problemStatus)})`
            : " (Cleared)"
        }`}
        description="Track the account problem status for this inventory."
        active={focus === "account-issues"}
        actions={
          <Button
            size="sm"
            onClick={() => {
              setProblemStatusInput(inventory?.problemStatus ?? "cleared");
              setProblemStatusRemarks("");
              setUpdateProblemStatusOpen(true);
            }}
            disabled={isArchived}
          >
            Update Status
          </Button>
        }
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>Updated Time</TableHead>
              <TableHead>Remarks</TableHead>
              <TableHead>Recorded By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {accountIssues.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center text-muted-foreground"
                >
                  No status history recorded yet.
                </TableCell>
              </TableRow>
            ) : (
              accountIssues.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{formatStatusLabel(entry.status)}</TableCell>
                  <TableCell>
                    {entry.createdAt?.toLocaleString() || "-"}
                  </TableCell>
                  <TableCell>{entry.remarks || "-"}</TableCell>
                  <TableCell>
                    {entry.recordedByUserEmail ||
                      entry.recordedByUserId ||
                      "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </WorkspaceSection>

      <WorkspaceSection
        id="sell-orders"
        title="Sell Orders"
        description="View and manage sell orders linked to this inventory."
        active={focus === "sell-orders"}
        actions={
          <Button size="sm" onClick={() => setCreateSoOpen(true)} disabled={isArchived}>
            New SO
          </Button>
        }
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Miles</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
              <TableHead>Invoices</TableHead>
              <TableHead className="w-[220px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {salesOrders.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-muted-foreground"
                >
                  No sell orders linked to this inventory.
                </TableCell>
              </TableRow>
            ) : (
              salesOrders.map((salesOrder) => (
                <TableRow key={salesOrder.id}>
                  <TableCell className="font-mono text-xs">
                    {formatShortId(salesOrder.id)}
                  </TableCell>
                  <TableCell>
                    {salesOrder.clientId ? (
                      <Link
                        className="text-primary underline-offset-4 hover:underline"
                        to={`/clients?clientId=${salesOrder.clientId}&soId=${salesOrder.id}`}
                      >
                        {salesOrder.clientDisplayName ||
                          salesOrder.clientEmail ||
                          "Unknown client"}
                      </Link>
                    ) : (
                      salesOrder.clientDisplayName ||
                      salesOrder.clientEmail ||
                      "Unknown client"
                    )}
                  </TableCell>
                  <TableCell>{formatStatusLabel(salesOrder.status)}</TableCell>
                  <TableCell className="text-right">
                    {formatNumber({ value: salesOrder.totalMilesSold })}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency({
                      value: getSalesOrderTotal({
                        revenue: salesOrder.revenue,
                        taxCost: salesOrder.taxCost,
                        totalAmount: salesOrder.totalAmount,
                      }),
                    })}
                  </TableCell>
                  <TableCell>{salesOrder.invoiceIds.length}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          openWorkspaceSheet({
                            kind: "salesOrder",
                            id: salesOrder.id,
                            sectionFocus: "sell-orders",
                          })
                        }
                      >
                        Open
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isArchived}
                        onClick={() => {
                          setDefaultSoInvoiceOrderId(salesOrder.id);
                          setCreateSoInvoiceOpen(true);
                        }}
                      >
                        New Invoice
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </WorkspaceSection>

      <WorkspaceSection
        id="sell-financials"
        title="Sell Order Financials"
        description="Manage receivables and sell-order-linked invoices in context."
        active={focus === "sell-financials"}
        actions={
          <Button
            size="sm"
            onClick={() => {
              setDefaultSoInvoiceOrderId(salesOrders[0]?.id || "");
              setCreateSoInvoiceOpen(true);
            }}
            disabled={isArchived || salesOrders.length === 0}
          >
            Add Invoice
          </Button>
        }
      >
        <div className="flex flex-wrap divide-y rounded-lg border mb-4 md:divide-x md:divide-y-0">
          {[
            {
              label: "Total",
              value: formatCurrency({ value: sellSummary.totalAmount }),
            },
            {
              label: "Invoiced",
              value: formatCurrency({ value: sellSummary.invoiced }),
            },
            {
              label: "Cleared",
              value: formatCurrency({ value: sellSummary.cleared }),
            },
            {
              label: "Outstanding",
              value: formatCurrency({ value: sellSummary.outstanding }),
            },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="flex flex-col gap-0.5 px-4 py-3 w-1/2 md:w-auto md:flex-1"
            >
              <span className="text-xs text-muted-foreground">{label}</span>
              <span className="text-sm font-semibold">{value}</span>
            </div>
          ))}
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Sell Order</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Amount Due</TableHead>
              <TableHead className="text-right">Cleared</TableHead>
              <TableHead className="text-right">Outstanding</TableHead>
              <TableHead className="w-[220px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sellInvoiceRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-muted-foreground"
                >
                  No sell-order invoices linked to this inventory yet.
                </TableCell>
              </TableRow>
            ) : (
              sellInvoiceRows.map((row) => (
                <TableRow key={row.invoice.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>
                        {row.linkedSalesOrder?.clientDisplayName ||
                          row.linkedSalesOrder?.clientEmail ||
                          "Unlinked"}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {formatShortId(row.invoice.soId)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{formatShortId(row.invoice.id)}</TableCell>
                  <TableCell>{formatStatusLabel(row.invoice.status)}</TableCell>
                  <TableCell className="text-right">
                    {formatCurrency({ value: row.invoice.amountDue })}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency({ value: row.cleared })}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency({ value: row.outstanding })}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          openWorkspaceSheet({
                            kind: "invoice",
                            id: row.invoice.id,
                            sectionFocus: "sell-financials",
                          })
                        }
                      >
                        Open Invoice
                      </Button>
                      {row.invoice.soId ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            openWorkspaceSheet({
                              kind: "salesOrder",
                              id: row.invoice.soId!,
                              sectionFocus: "sell-orders",
                            })
                          }
                        >
                          Open SO
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </WorkspaceSection>

      <WorkspaceSection
        id="audit-log"
        title="Audit Log"
        description="Track every important state change tied to this inventory."
        active={focus === "audit-log"}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Action</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Summary</TableHead>
              <TableHead>Performed By</TableHead>
              <TableHead>Timestamp</TableHead>
              <TableHead>Time Ago</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {auditLogs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-muted-foreground"
                >
                  No audit log entries recorded for this inventory yet.
                </TableCell>
              </TableRow>
            ) : (
              auditLogs.map((auditLog) => {
                const visibleChanges = auditLog.changes
                  ? Object.entries(auditLog.changes).filter(
                      ([key]) => key !== "updatedAt",
                    )
                  : [];
                const isUpdate = auditLog.action === AuditLogAction.UPDATE;
                const isExpandable = isUpdate && visibleChanges.length > 0;
                const isExpanded = expandedAuditLogId === auditLog.id;

                return (
                  <Fragment key={auditLog.id}>
                    <TableRow
                      className={isExpandable ? "cursor-pointer hover:bg-muted/40" : undefined}
                      onClick={
                        isExpandable
                          ? () =>
                              setExpandedAuditLogId(
                                isExpanded ? null : auditLog.id,
                              )
                          : undefined
                      }
                    >
                      <TableCell className="w-8 text-muted-foreground">
                        {isExpandable ? (
                          isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {formatStatusLabel(auditLog.actionType || auditLog.action)}
                      </TableCell>
                      <TableCell>
                        {getAuditEntityLabel(auditLog.entityType)} ·{" "}
                        {formatShortId(auditLog.entityId || auditLog.documentId)}
                      </TableCell>
                      <TableCell>{auditLog.summary || "-"}</TableCell>
                      <TableCell>
                        {getAuditPerformedByLabel({
                          performedByType: auditLog.performedByType,
                          user: auditLog.user,
                        })}
                      </TableCell>
                      <TableCell>
                        {auditLog.createdAt?.toLocaleString() || "-"}
                      </TableCell>
                      <TableCell>
                        {formatRelativeTimestamp(auditLog.createdAt)}
                      </TableCell>
                    </TableRow>
                    {isExpandable && isExpanded ? (
                      <TableRow className="bg-muted/20 hover:bg-muted/20">
                        <TableCell />
                        <TableCell colSpan={6} className="py-3">
                          <div className="overflow-hidden rounded-md border">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                                  <th className="px-3 py-2 text-left font-medium w-1/4">
                                    Field
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    Old
                                  </th>
                                  <th className="w-6" />
                                  <th className="px-3 py-2 text-left font-medium">
                                    New
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {visibleChanges.map(([fieldName, change]) => {
                                  const fromEmpty =
                                    change.from === null || change.from === undefined;
                                  const toEmpty =
                                    change.to === null || change.to === undefined;
                                  const fromText = fromEmpty
                                    ? ""
                                    : formatAuditValue(change.from);
                                  const toText = toEmpty
                                    ? ""
                                    : formatAuditValue(change.to);
                                  const isMultiline =
                                    fromText.includes("\n") || toText.includes("\n");
                                  const valueCellClass = isMultiline
                                    ? "px-3 py-2 font-mono text-xs whitespace-pre-wrap break-all"
                                    : "px-3 py-2 whitespace-pre-wrap break-words";
                                  return (
                                    <tr
                                      key={fieldName}
                                      className="border-b last:border-b-0 align-top"
                                    >
                                      <td className="px-3 py-2 font-medium text-foreground whitespace-nowrap">
                                        {humanizeFieldName(fieldName)}
                                      </td>
                                      <td
                                        className={`${valueCellClass} text-muted-foreground`}
                                      >
                                        {fromEmpty ? (
                                          <span className="italic">—</span>
                                        ) : (
                                          fromText
                                        )}
                                      </td>
                                      <td className="px-1 py-2 text-muted-foreground">
                                        <ArrowRight className="h-3.5 w-3.5" />
                                      </td>
                                      <td className={`${valueCellClass} text-foreground`}>
                                        {toEmpty ? (
                                          <span className="italic text-muted-foreground">
                                            —
                                          </span>
                                        ) : (
                                          toText
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </WorkspaceSection>

      <InventoryWorkspacePurchaseOrderCreateSheet
        open={createPoOpen}
        onOpenChange={setCreatePoOpen}
        inventory={inventory}
        vendors={vendors}
        onCreated={(poId) =>
          openWorkspaceSheet({
            kind: "purchaseOrder",
            id: poId,
            sectionFocus: "purchase-orders",
          })
        }
      />

      <InventoryWorkspaceSalesOrderCreateSheet
        open={createSoOpen}
        onOpenChange={setCreateSoOpen}
        inventory={inventory}
        clients={clients}
        users={users}
        onCreated={(soId) =>
          openWorkspaceSheet({
            kind: "salesOrder",
            id: soId,
            sectionFocus: "sell-orders",
          })
        }
      />

      <InventoryWorkspaceInvoiceCreateSheet
        open={createPoInvoiceOpen}
        onOpenChange={setCreatePoInvoiceOpen}
        kind="po"
        purchaseOrders={purchaseOrders}
        salesOrders={salesOrders}
        invoices={invoices}
        clearedByInvoiceId={clearedByInvoiceId}
        defaultOrderId={defaultPoInvoiceOrderId}
        onCreated={(invoiceId) =>
          openWorkspaceSheet({
            kind: "invoice",
            id: invoiceId,
            sectionFocus: "purchase-financials",
          })
        }
      />

      <InventoryWorkspaceInvoiceCreateSheet
        open={createSoInvoiceOpen}
        onOpenChange={setCreateSoInvoiceOpen}
        kind="so"
        purchaseOrders={purchaseOrders}
        salesOrders={salesOrders}
        invoices={invoices}
        clearedByInvoiceId={clearedByInvoiceId}
        defaultOrderId={defaultSoInvoiceOrderId}
        onCreated={(invoiceId) =>
          openWorkspaceSheet({
            kind: "invoice",
            id: invoiceId,
            sectionFocus: "sell-financials",
          })
        }
      />

      <InventoryWorkspaceInventoryEditSheet
        open={editInventoryOpen}
        onOpenChange={setEditInventoryOpen}
        inventory={inventory}
        vendors={vendors}
      />

      <InventoryWorkspaceStockCheckSheet
        open={stockCheckOpen}
        onOpenChange={setStockCheckOpen}
        inventory={inventory}
      />

      <Dialog
        open={updateProblemStatusOpen}
        onOpenChange={setUpdateProblemStatusOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Account Issue Status</DialogTitle>
            <DialogDescription>
              Pick the new problem status for this inventory and add a remark
              explaining the change.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>New Status</Label>
              <Select
                value={problemStatusInput}
                onValueChange={(value) =>
                  setProblemStatusInput(value as typeof problemStatusInput)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cleared">Cleared</SelectItem>
                  <SelectItem value="audited">Audited</SelectItem>
                  <SelectItem value="issue">Issue</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Remarks</Label>
              <Textarea
                value={problemStatusRemarks}
                onChange={(event) =>
                  setProblemStatusRemarks(event.target.value)
                }
                placeholder="What was observed or done?"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUpdateProblemStatusOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!inventoryId) {
                  return;
                }
                await recordAccountIssueMutation.mutateAsync({
                  inventoryId,
                  status: problemStatusInput,
                  remarks: problemStatusRemarks.trim() || undefined,
                });
                setUpdateProblemStatusOpen(false);
              }}
              loading={recordAccountIssueMutation.isPending}
              loadingText="Saving..."
              disabled={!inventoryId}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PurchaseOrderSheet
        poId={activeSheetType === "purchaseOrder" ? selectedPoId : ""}
        open={activeSheetType === "purchaseOrder"}
        onOpenChange={(open) => {
          if (!open) {
            closeWorkspaceSheet("purchaseOrder");
          }
        }}
      />

      <SalesOrderSheet
        soId={activeSheetType === "salesOrder" ? selectedSoId : ""}
        open={activeSheetType === "salesOrder"}
        onOpenChange={(open) => {
          if (!open) {
            closeWorkspaceSheet("salesOrder");
          }
        }}
      />

      <InvoiceSheet
        invoiceId={activeSheetType === "invoice" ? selectedInvoiceId : ""}
        open={activeSheetType === "invoice"}
        onOpenChange={(open) => {
          if (!open) {
            closeWorkspaceSheet("invoice");
          }
        }}
      />
    </div>
  );
}
